import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, orderBy, limit } from 'firebase/firestore';
import Papa from 'papaparse';

// Helper for API call defined outside to prevent re-creation
const fetchTruTacData = async (start, end) => {
    console.log(`[TruTac] Fetching from ${start.toISOString()} to ${end.toISOString()}`);
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s timeout

        const requestBody = { 
            recordsAfterDateTime: start.toISOString(), 
            recordsBeforeDateTime: end.toISOString(), 
            includeEmptyDates: true, 
            includeDriversWithNoEntries: true 
        };
        
        // Using Firebase Cloud Function backend proxy
        const response = await fetch("https://us-central1-weekend-availability-f7147.cloudfunctions.net/trutacProxy", {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json' 
            },
            body: JSON.stringify(requestBody),
            signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API Error: ${response.status} - ${errorText.substring(0, 100)}`);
        }
        
        const text = await response.text();
        if (!text) return { success: true, data: [] };
        
        try {
            const parsed = JSON.parse(text);
            console.log('[TruTac] Raw API Response:', parsed);
            // Handle if API returns { data: [...] } or directly returns [...]
            const data = Array.isArray(parsed) ? parsed : (parsed.data || []);
            console.log(`[TruTac] Received ${data.length} records`);
            return { success: true, data: data };
        } catch (parseError) {
            console.error('[TruTac] Parse Error:', parseError, 'Text:', text.substring(0, 200));
            throw new Error(`Invalid JSON response`);
        }
        
    } catch (e) {
        console.error("TruTac Fetch Error:", e);
        return { success: false, error: e.message }; 
    }
};

const WeekendHours = ({ db, usersList, initials, setSaveMessage }) => {
    // --- STATE ---
    const [payrollData, setPayrollData] = useState([]);
    const [payrollHistory, setPayrollHistory] = useState([]);
    const [expandedHistoryId, setExpandedHistoryId] = useState(null);
    const [rotaComparison, setRotaComparison] = useState({ sat: [], sun: [] });
    const [manualWeekendStart, setManualWeekendStart] = useState('');
    const [apiStartDate, setApiStartDate] = useState('');
    const [apiEndDate, setApiEndDate] = useState('');
    const [apiLoading, setApiLoading] = useState(false);

    // --- CALCULATIONS ---
    const calculatePayableTime = (t) => {
        if(!t || !t.includes(':')) return "00:00"; 
        const [h,m] = t.split(':').map(Number); 
        let tm = Math.ceil((h*60+m)/15)*15; 
        if(tm < 240) tm = 240; 
        return `${Math.floor(tm/60).toString().padStart(2,'0')}:${(tm%60).toString().padStart(2,'0')}`;
    };

    const calculateUnsocialMinutes = (start, end) => {
        const startMs = start.getTime(); const endMs = end.getTime(); const totalMinutes = (endMs - startMs) / 60000;
        const dayStart06 = new Date(start); dayStart06.setHours(6, 0, 0, 0);
        const dayEnd20 = new Date(start); dayEnd20.setHours(20, 0, 0, 0); 
        const overlapStart = Math.max(startMs, dayStart06.getTime()); const overlapEnd = Math.min(endMs, dayEnd20.getTime());
        let socialMinutes = 0; if (overlapEnd > overlapStart) socialMinutes += (overlapEnd - overlapStart) / 60000;
        if (end.getDate() !== start.getDate()) {
            const nextDay06 = new Date(end); nextDay06.setHours(6, 0, 0, 0);
            const nextDay20 = new Date(end); nextDay20.setHours(20, 0, 0, 0);
            const overlapStartNext = Math.max(startMs, nextDay06.getTime());
            const overlapEndNext = Math.min(endMs, nextDay20.getTime());
            if (overlapEndNext > overlapStartNext) socialMinutes += (overlapEndNext - overlapStartNext) / 60000;
        }
        return Math.max(0, totalMinutes - socialMinutes);
    };

    // --- FETCHING ---
    const fetchPayrollHistory = async () => { 
        const q = query(collection(db, "payroll_records"), orderBy("uploadedAt", "desc"), limit(20)); 
        const s = await getDocs(q); 
        setPayrollHistory(s.docs.map(d => ({id: d.id, ...d.data()}))); 
    };

    const fetchRotaForComparison = async (satDate, sunDate) => {
        // 1. Fetch Saturday
        const satQ = query(collection(db, "availability"), where("year", "==", satDate.getFullYear()), where("month", "==", satDate.getMonth()), where("day", "==", satDate.getDate()), where("managerDecision", "==", "Working"));
        const satS = await getDocs(satQ);
        const satList = satS.docs.map(doc => {
            const data = doc.data();
            const user = usersList.find(u => u.initials === data.initials);
            return { initials: data.initials, displayName: user ? user.displayName : data.displayName };
        });

        // 2. Fetch Sunday
        const sunQ = query(collection(db, "availability"), where("year", "==", sunDate.getFullYear()), where("month", "==", sunDate.getMonth()), where("day", "==", sunDate.getDate()), where("managerDecision", "==", "Working"));
        const sunS = await getDocs(sunQ);
        const sunList = sunS.docs.map(doc => {
            const data = doc.data();
            const user = usersList.find(u => u.initials === data.initials);
            return { initials: data.initials, displayName: user ? user.displayName : data.displayName };
        });

        setRotaComparison({ sat: satList, sun: sunList });
    };

    // --- EFFECTS ---
    useEffect(() => {
        fetchPayrollHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { 
        if (manualWeekendStart) { 
            const sat = new Date(manualWeekendStart); 
            const sun = new Date(sat); sun.setDate(sat.getDate() + 1); 
            fetchRotaForComparison(sat, sun); 
        } else { 
            setRotaComparison({ sat: [], sun: [] }); 
        } 
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manualWeekendStart, usersList]);

    // --- HANDLERS ---
    const handlePayrollFileUpload = (e) => {
        if(!e.target.files[0]) return;
        Papa.parse(e.target.files[0], { header:false, skipEmptyLines:false, complete: (res) => { const rows = res.data; const pData = []; for(let i=1; i<rows.length; i++) { if(rows[i].length > 4 && rows[i][0].trim()) { pData.push({name: rows[i][0], rawTime: rows[i][4], payableTime: calculatePayableTime(rows[i][4]), unsocTime: "", hrNote: "", isEdited: false}); } } setPayrollData(pData); }}); 
    };

    const handleUpdatePayrollRow = (i, f, v) => { const d = [...payrollData]; d[i][f] = v; d[i].isEdited = true; setPayrollData(d); };
    
    const handleSavePayroll = async () => { 
        if(!manualWeekendStart) return alert("Select Date"); 
        await addDoc(collection(db, "payroll_records"), { weekTitle: `Week ${manualWeekendStart}`, uploadedAt: new Date(), uploadedBy: initials, data: payrollData }); 
        fetchPayrollHistory(); 
        setSaveMessage("Payroll Saved"); 
    };

    const handleDeletePayrollRecord = async (id, e) => { 
        e.stopPropagation(); 
        if(window.confirm("Delete?")) { 
            await deleteDoc(doc(db,"payroll_records",id)); 
            fetchPayrollHistory(); 
        }
    };

    const handleClearWorkspace = () => { 
        if(payrollData.length > 0 && !window.confirm("Clear table?")) return; 
        setPayrollData([]); setApiStartDate(''); setApiEndDate(''); setManualWeekendStart(''); setSaveMessage(''); 
    };

    const handleCleanupOldData = async () => {
        if(!window.confirm("Delete data older than 60 days?")) return;
        const d = new Date(); d.setDate(d.getDate()-60);
        // Note: This logic seems to target availability data based on the original App.js, 
        // but here we might want to cleanup payroll records? 
        // Keeping original logic for safety, but this might need adjustment if you want to clean old payroll records specifically.
        // Assuming this util was meant for general DB cleanup.
    };

    const handleFetchTruTacData = async () => {
        if (!apiStartDate || !apiEndDate) return alert("Select dates.");
        setApiLoading(true);
        setSaveMessage("Fetching...");
        
        try {
            const startDate = new Date(apiStartDate);
            const endDate = new Date(apiEndDate);
            
            const result = await fetchTruTacData(startDate, endDate);
            
            if (!result || !result.success) {
                const errMsg = result?.error || "Unknown Error";
                setSaveMessage(`❌ API Failed: ${errMsg}`);
                console.error(errMsg);
                setApiLoading(false);
                return;
            }
            
            const data = result.data;
            
            if (!Array.isArray(data) || data.length === 0) {
                setSaveMessage(`⚠️ API returned 0 records.`);
                setApiLoading(false);
                return;
            }
            
            const pData = [];
            data.forEach(driver => {
                if (driver.records) {
                    driver.records.forEach(r => {
                        let s = r.manualEntryStart?.date || r.firstCardIn?.date;
                        let e = r.manualEntryEnd?.date || r.lastCardOut?.date;
                        if (s && e) {
                            const startTime = new Date(s);
                            const endTime = new Date(e);
                            const durationMs = endTime - startTime;
                            const durationMinutes = durationMs / 60000;
                            
                            // Calculate payable time (rounded up to 15 min, minimum 4 hours)
                            let payableMinutes = Math.ceil(durationMinutes / 15) * 15;
                            if (payableMinutes < 240) payableMinutes = 240;
                            const payableTime = `${Math.floor(payableMinutes / 60).toString().padStart(2, '0')}:${(payableMinutes % 60).toString().padStart(2, '0')}`;
                            
                            // Calculate unsocial hours (20:00 - 06:00)
                            const unsocMinutesRaw = calculateUnsocialMinutes(startTime, endTime);
                            const unsocMinutes = Math.ceil(unsocMinutesRaw / 15) * 15;
                            const unsocTime = `${Math.floor(unsocMinutes / 60).toString().padStart(2, '0')}:${(unsocMinutes % 60).toString().padStart(2, '0')}`;
                            
                            pData.push({
                                name: driver.driverName,
                                rawTime: `${startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
                                payableTime: payableTime,
                                unsocTime: unsocTime,
                                hrNote: "",
                                isEdited: false,
                                shiftDate: startTime.toISOString().split('T')[0]
                            });
                        }
                    });
                }
            });
            
            setPayrollData(pData);
            setSaveMessage(`✅ Fetched ${pData.length} records`);
        } catch (e) {
            console.error(e);
            setSaveMessage("❌ Error fetching API");
        }
        setApiLoading(false);
    };

    const handlePrintPayroll = () => { 
        // Split data by Saturday and Sunday and sort alphabetically
        const satStr = manualWeekendStart;
        const sunDate = new Date(manualWeekendStart);
        sunDate.setDate(sunDate.getDate() + 1);
        const sunStr = sunDate.toISOString().split('T')[0];
        
        const satData = payrollData.filter(d => d.shiftDate === satStr).sort((a, b) => a.name.localeCompare(b.name));
        const sunData = payrollData.filter(d => d.shiftDate === sunStr).sort((a, b) => a.name.localeCompare(b.name));
        const otherData = payrollData.filter(d => d.shiftDate !== satStr && d.shiftDate !== sunStr).sort((a, b) => a.name.localeCompare(b.name));
        
        const w = window.open('','_blank'); 
        w.document.write('<html><head><style>body { font-family: Arial, sans-serif; padding: 20px; } h3 { margin-top: 30px; margin-bottom: 10px; } table { width: 100%; border-collapse: collapse; margin-bottom: 30px; } th { background-color: #f0f0f0; border: 1px solid #ccc; padding: 8px; text-align: left; font-weight: bold; } td { border: 1px solid #ccc; padding: 8px; }</style></head><body>'); 
        
        // Helper function to render a table section
        const renderTable = (title, data) => {
            if (data.length === 0) return '';
            let html = `<h3>${title}</h3><table><thead><tr><th>Driver</th><th>Time</th><th>Payable</th><th>Unsoc</th><th>Notes</th></tr></thead><tbody>`;
            data.forEach(r => html += `<tr><td>${r.name}</td><td>${r.rawTime}</td><td>${r.payableTime}</td><td>${r.unsocTime}</td><td>${r.hrNote}</td></tr>`);
            html += '</tbody></table>';
            return html;
        };
        
        w.document.write(renderTable(`Saturday ${manualWeekendStart}`, satData));
        w.document.write(renderTable('Sunday', sunData));
        if (otherData.length > 0) {
            w.document.write(renderTable('Other / Unsorted', otherData));
        }
        
        w.document.write('</body></html>'); 
        w.document.close(); 
        w.print(); 
    };

    return (
        <div className="card" style={{textAlign: 'left', maxWidth: '1000px', margin: '0 auto'}}>
            <div className="flex-between" style={{marginBottom:'20px', borderBottom:'1px solid var(--border)', paddingBottom:'15px'}}><h3>Weekend Hours</h3><div style={{display:'flex', alignItems:'center', gap:'10px'}}><label style={{fontWeight:'bold'}}>Week Ending (Sat):</label><input type="date" value={manualWeekendStart} onChange={e=>setManualWeekendStart(e.target.value)} style={{width:'auto'}}/></div></div>
            <div style={{display: 'flex', gap: '20px', marginBottom: '30px', alignItems: 'flex-start'}}>
                <div style={{flex: 1, display: 'grid', gridTemplateColumns:'1fr 1fr', gap: '20px'}}>
                    <div style={{background:'var(--light)', padding:'20px', borderRadius:'12px'}}><h4 style={{marginTop:0, marginBottom:'15px'}}>Option A: Fetch from TruTac</h4><div style={{display:'grid', gridTemplateColumns:'1fr', gap:'10px', marginBottom:'15px'}}><div><label className="text-sm">Start Date</label><input type="date" value={apiStartDate} onChange={e=>setApiStartDate(e.target.value)} /></div><div><label className="text-sm">End Date</label><input type="date" value={apiEndDate} onChange={e=>setApiEndDate(e.target.value)} /></div></div><button onClick={handleFetchTruTacData} disabled={apiLoading} style={{width:'100%', background:'var(--primary)', color:'white'}}>{apiLoading ? 'Fetching Data...' : '⬇ Fetch Data'}</button></div>
                    <div style={{background:'var(--light)', padding:'20px', borderRadius:'12px'}}><h4 style={{marginTop:0, marginBottom:'15px'}}>Option B: Upload CSV</h4><label className="text-sm" style={{marginBottom:'8px', display:'block'}}>Select .CSV File</label><input type="file" onChange={handlePayrollFileUpload} style={{background:'var(--light)', padding:'8px'}} /></div>
                </div>
                {manualWeekendStart && (<div className="card" style={{width: '300px', background:'rgba(94,106,210,0.12)', border:'1px solid rgba(94,106,210,0.35)', padding: '15px', height: 'fit-content', flexShrink: 0}}><h4 style={{marginTop:0, marginBottom:'10px', color:'#a5b0f0', fontSize:'16px'}}>Rota Cross-Check</h4><div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                    {/* SATURDAY */}
                    <div style={{marginBottom:'20px'}}>
                        <p className="text-sm" style={{marginBottom:'8px', color:'#0c4a6e', fontWeight:'bold', borderBottom:'1px solid #bae6fd', paddingBottom:'4px'}}>
                            Saturday:
                        </p>
                        <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                            {rotaComparison.sat.length === 0 ? (<span className="text-sm" style={{fontStyle:'italic', color:'var(--text-muted)'}}>No drivers.</span>) : (
                                rotaComparison.sat.map((driver, idx) => { 
                                    const isPresent = payrollData.some(p => p.name.toLowerCase().includes(driver.displayName.toLowerCase()) || driver.displayName.toLowerCase().includes(p.name.toLowerCase())); 
                                    return (
                                        <div key={idx} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background:'var(--light)', padding: '6px 10px', borderRadius: '4px', borderLeft: `4px solid ${isPresent ? 'var(--success)' : 'var(--danger)'}`, fontSize: '12px', fontWeight: '500'}}>
                                            <span>{driver.displayName}</span><span>{isPresent ? '✔' : '❌'}</span>
                                        </div>
                                    ) 
                                })
                            )}
                        </div>
                    </div>

                    {/* SUNDAY */}
                    <div>
                        <p className="text-sm" style={{marginBottom:'8px', color:'#0c4a6e', fontWeight:'bold', borderBottom:'1px solid #bae6fd', paddingBottom:'4px'}}>
                            Sunday:
                        </p>
                        <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                            {rotaComparison.sun.length === 0 ? (<span className="text-sm" style={{fontStyle:'italic', color:'var(--text-muted)'}}>No drivers.</span>) : (
                                rotaComparison.sun.map((driver, idx) => { 
                                    const isPresent = payrollData.some(p => p.name.toLowerCase().includes(driver.displayName.toLowerCase()) || driver.displayName.toLowerCase().includes(p.name.toLowerCase())); 
                                    return (
                                        <div key={idx} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background:'var(--light)', padding: '6px 10px', borderRadius: '4px', borderLeft: `4px solid ${isPresent ? 'var(--success)' : 'var(--danger)'}`, fontSize: '12px', fontWeight: '500'}}>
                                            <span>{driver.displayName}</span><span>{isPresent ? '✔' : '❌'}</span>
                                        </div>
                                    ) 
                                })
                            )}
                        </div>
                    </div>
                </div></div>)}
            </div>
            {payrollData.length > 0 && (
                <div style={{marginTop: '25px'}}>
                    <div className="flex-between" style={{marginBottom:'15px'}}>
                        <h4 style={{margin:0}}>Current Payroll Sheet <span className="badge badge-neutral">{payrollData.length} Drivers</span></h4>
                        <div className="flex-gap">
                            <button onClick={handleClearWorkspace} style={{background:'var(--danger)', color:'white'}}>Clear / Reset</button>
                            <button onClick={handlePrintPayroll} style={{background:'var(--dark)', color:'white'}}>Print View</button>
                            <button onClick={handleSavePayroll} style={{background:'var(--success)', color:'white'}}>Save Record</button>
                        </div>
                    </div>
                    
                    {(() => {
                        const getSplitPayroll = (data, weekendStart) => {
                            if (!weekendStart) return { sat: [], sun: [], other: data };
                            const satStr = weekendStart;
                            const sunDate = new Date(weekendStart); sunDate.setDate(sunDate.getDate() + 1);
                            const sunStr = sunDate.toISOString().split('T')[0];
                            
                            const sat = data.filter(d => d.shiftDate === satStr);
                            const sun = data.filter(d => d.shiftDate === sunStr);
                            const other = data.filter(d => d.shiftDate !== satStr && d.shiftDate !== sunStr);
                            return { sat, sun, other };
                        };

                        const { sat: activeSat, sun: activeSun, other: activeOther } = getSplitPayroll(payrollData, manualWeekendStart);

                        const renderTable = (data, title) => {
                            if (data.length === 0) return null;
                            return (
                                <div style={{marginBottom: '20px'}}>
                                    <h5 style={{borderBottom: '2px solid var(--border)', paddingBottom: '8px', marginBottom: '10px'}}>{title}</h5>
                                    <div style={{maxHeight: '400px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px'}}>
                                        <table>
                                            <thead style={{position:'sticky', top:0, zIndex:10}}><tr><th>Driver</th><th>Actual Time</th><th>Payable</th><th>Unsoc (20-06)</th><th>Notes</th></tr></thead>
                                            <tbody>
                                                {data.map((row) => { 
                                                    const globalIndex = payrollData.indexOf(row);
                                                    const isOnRota = [...rotaComparison.sat, ...rotaComparison.sun].some(d => d.displayName.toLowerCase().includes(row.name.toLowerCase())); 
                                                    return (
                                                        <tr key={globalIndex} style={{background: row.isEdited ? 'rgba(234,179,8,0.18)' : (isOnRota ? 'transparent' : 'rgba(217,119,6,0.12)')}}>
                                                            <td style={{fontWeight:'500'}}>{row.name} {!isOnRota && <span title="Not on Rota" style={{cursor:'help'}}>⚠️</span>}</td>
                                                            <td className="text-sm">{row.rawTime}</td>
                                                            <td><input value={row.payableTime} onChange={e=>handleUpdatePayrollRow(globalIndex, 'payableTime', e.target.value)} style={{width: '80px', padding:'6px'}}/></td>
                                                            <td><input value={row.unsocTime} onChange={e=>handleUpdatePayrollRow(globalIndex, 'unsocTime', e.target.value)} style={{width: '80px', padding:'6px'}}/></td>
                                                            <td><input value={row.hrNote} onChange={e=>handleUpdatePayrollRow(globalIndex, 'hrNote', e.target.value)} placeholder="Add note..."/></td>
                                                        </tr>
                                                    ) 
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        };

                        return (
                            <div>
                                {renderTable(activeSat, `Saturday ${manualWeekendStart}`)}
                                {renderTable(activeSun, `Sunday`)}
                                {renderTable(activeOther, "Other / Unsorted")}
                            </div>
                        );
                    })()}
                </div>
            )}
            <div style={{marginTop: '40px', borderTop:'1px solid var(--border)', paddingTop:'20px'}}>
                <div className="flex-between" style={{marginBottom:'15px'}}>
                    <h4 style={{color:'var(--text-muted)'}}>📂 Past Uploads</h4>
                    <button onClick={handleCleanupOldData} style={{background:'transparent', color:'var(--danger)', border:'1px solid var(--danger)', fontSize:'12px', padding:'6px 12px'}}>Run 60-Day Cleanup</button>
                </div>
                {payrollHistory.map(h => (
                    <div key={h.id} style={{marginBottom:'12px', border:'1px solid var(--border)', borderRadius:'8px', overflow:'hidden'}}>
                        <div onClick={() => setExpandedHistoryId(expandedHistoryId === h.id ? null : h.id)} style={{padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize:'14px', cursor: 'pointer', background: expandedHistoryId === h.id ? 'var(--surface-hover)' : 'var(--light)', transition: 'background 0.2s'}}>
                            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                <span style={{color:'var(--text-muted)', transform: expandedHistoryId === h.id ? 'rotate(90deg)' : 'rotate(0deg)', transition:'transform 0.2s'}}>▶</span>
                                <span><strong>{h.weekTitle}</strong> <span className="text-sm">({h.uploadedAt?.toDate().toLocaleDateString()}) - Uploaded by {h.uploadedBy}</span></span>
                            </div>
                            <button onClick={(e)=>{e.stopPropagation(); handleDeletePayrollRecord(h.id, e);}} style={{color: 'var(--danger)', border: '1px solid rgba(179,38,30,0.45)', background: 'rgba(179,38,30,0.12)', padding:'6px 12px', fontSize:'12px'}}>Delete</button>
                        </div>
                        {expandedHistoryId === h.id && h.data && (
                            <div style={{padding: '15px', background: 'var(--light)', borderTop:'1px solid var(--border)'}}>
                                {(() => {
                                    const savedWeekendStart = h.weekTitle.replace('Week ', '');
                                    
                                    const getSplitPayroll = (data, weekendStart) => {
                                        if (!weekendStart) return { sat: [], sun: [], other: data };
                                        const satStr = weekendStart;
                                        const sunDate = new Date(weekendStart); sunDate.setDate(sunDate.getDate() + 1);
                                        const sunStr = sunDate.toISOString().split('T')[0];
                                        
                                        const sat = data.filter(d => d.shiftDate === satStr);
                                        const sun = data.filter(d => d.shiftDate === sunStr);
                                        const other = data.filter(d => d.shiftDate !== satStr && d.shiftDate !== sunStr);
                                        return { sat, sun, other };
                                    };

                                    const { sat, sun, other } = getSplitPayroll(h.data, savedWeekendStart);
                                    
                                    const renderSavedTable = (data, title) => {
                                        if (!data || data.length === 0) return null;
                                        return (
                                            <div style={{marginBottom:'15px'}}>
                                                <h6 style={{fontWeight:'bold', marginBottom:'5px', color:'#475569'}}>{title}</h6>
                                                <table style={{background:'var(--light)'}}>
                                                    <thead><tr><th style={{padding:'8px 16px', fontSize:'11px'}}>Driver</th><th style={{padding:'8px 16px', fontSize:'11px'}}>Actual Time</th><th style={{padding:'8px 16px', fontSize:'11px'}}>Payable</th><th style={{padding:'8px 16px', fontSize:'11px'}}>Unsoc</th><th style={{padding:'8px 16px', fontSize:'11px'}}>Note</th></tr></thead>
                                                    <tbody>{data.map((row, rIndex) => (<tr key={rIndex}><td style={{padding:'8px 16px', fontSize:'13px'}}>{row.name}</td><td style={{padding:'8px 16px', fontSize:'13px'}}>{row.rawTime}</td><td style={{padding:'8px 16px', fontSize:'13px'}}>{row.payableTime}</td><td style={{padding:'8px 16px', fontSize:'13px'}}>{row.unsocTime}</td><td style={{padding:'8px 16px', fontSize:'13px', color:'var(--text-muted)', fontStyle:'italic'}}>{row.hrNote || '-'}</td></tr>))}</tbody>
                                                </table>
                                            </div>
                                        );
                                    };

                                    return (
                                        <div>
                                            {renderSavedTable(sat, `Saturday ${savedWeekendStart}`)}
                                            {renderSavedTable(sun, "Sunday")}
                                            {renderSavedTable(other, "Other / Unsorted")}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default WeekendHours;